import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface SessionInterruptedModalProps {
  isOpen: boolean;
  onViewReport: () => void | Promise<void>;
  onBackToSimulations: () => void | Promise<void>;
}

export default function SessionInterruptedModal({
  isOpen,
  onViewReport,
  onBackToSimulations,
}: SessionInterruptedModalProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<'report' | 'back' | null>(null);

  const handleViewReport = async () => {
    if (busy) return;
    setBusy('report');
    try {
      await onViewReport();
    } finally {
      setBusy(null);
    }
  };

  const handleBack = async () => {
    if (busy) return;
    setBusy('back');
    try {
      await onBackToSimulations();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={false}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-destructive" aria-hidden="true" />
            {t('simulationPage.interruptedModalTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('simulationPage.interruptedModalDescription')}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={busy !== null}
            aria-busy={busy === 'back'}
          >
            {t('simulationPage.interruptedBackToSimulations')}
          </Button>
          <Button
            type="button"
            onClick={handleViewReport}
            disabled={busy !== null}
            aria-busy={busy === 'report'}
          >
            {t('simulationPage.interruptedViewReport')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
